import moment from 'moment';
import React, { Fragment, useMemo } from 'react';
import Typography from '~components/typography';
import { BookingStatusOption, CategoryOption } from '~graphql/__generated__/globalTypes';
import { SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings } from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { Trans, useTranslation } from '~i18n';
import { Time } from '~svg-components';
import { useCountdownText } from '~utils/count-down-time';
import { usePaymentItemsIsAwaiting } from '~utils/use-bank-transfer';

interface IProps {
  booking: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings;
}

export const NominationCountdown: React.FC<IProps> = ({ booking }) => {
  const { t } = useTranslation('student_portal');

  const { heldToTime, countDown, paymentItems, partiallyPaidByBankTransferBeforeCheckIn } = booking;
  const endTimeAsMoment = useMemo(() => moment(heldToTime || countDown), [countDown, heldToTime]);
  const countDownResult = useCountdownText(endTimeAsMoment, 30000);

  const hasAwaitingPaymentItems = usePaymentItemsIsAwaiting(paymentItems);

  const renderCountDown = useMemo(() => {
    if (!hasAwaitingPaymentItems) {
      if (partiallyPaidByBankTransferBeforeCheckIn) {
        return (
          <Typography
            component="p"
            variant="body3"
            style={{ flex: 1, paddingLeft: 8, display: 'flex', flexDirection: 'column' }}
          >
            <span>
              <Trans i18nKey={'payment.bank_transfer_underpaid_countdown'} ns="student_portal">
                It is noticed that the payment
                <Typography
                  variant="body3"
                  component="span"
                  color="var(--color-red_6)"
                  fontWeight={600}
                >
                  {{ time: countDownResult.trim() === '' ? '0s' : countDownResult }}
                </Typography>
              </Trans>
            </span>
            <Typography textDecoration="underline" cursor="pointer" variant="body3">
              {t('go_to_payment')}
            </Typography>
          </Typography>
        );
      }
      return (
        <Typography component="p" variant="body3" style={{ flex: 1, paddingLeft: 8 }}>
          <Trans i18nKey={'nomination.countDown'} ns="public">
            Your offer will expire in
            <Typography
              variant="body3"
              component="span"
              color="var(--color-red_6)"
              fontWeight={600}
            >
              {{ time: countDownResult.trim() === '' ? '0s' : countDownResult }}
            </Typography>
          </Trans>
        </Typography>
      );
    }
    if (!partiallyPaidByBankTransferBeforeCheckIn) {
      return (
        <Typography component="p" variant="body3" style={{ flex: 1, paddingLeft: 8 }}>
          <Trans i18nKey={'nomination.countDown'} ns="public">
            Your offer will expire in
            <Typography
              variant="body3"
              component="span"
              color="var(--color-red_6)"
              fontWeight={600}
            >
              {{ time: countDownResult.trim() === '' ? '0s' : countDownResult }}
            </Typography>
          </Trans>
          <Typography component="span" variant="body3">
            .{t('payment.bank_transfer.countDown_awaiting')}
          </Typography>
        </Typography>
      );
    }
  }, [countDownResult, hasAwaitingPaymentItems, partiallyPaidByBankTransferBeforeCheckIn, t]);

  return (
    <div className="count-down-component">
      <div className="count-down-component__icon">
        <Time fill="var(--color-red_6)" />
      </div>
      {renderCountDown}
      <style jsx>
        {`
          .count-down-component {
            width: 100%;
            display: flex;
            align-items: flex-start;
            padding: 10px 12px;
            border-radius: 14px;
            background-color: var(--color-red_1);
            margin-top: 12px;
            &__icon {
              width: 20px;
              height: 19px;
              margin-top: 2px;
            }
          }
          @media screen and (min-width: 768px) {
            .count-down-component {
              padding: 16px;
              border-radius: 14px;
            }
          }
        `}
      </style>
    </div>
  );
};

export const DirectLetCountDown: React.FC<IProps> = ({ booking }) => {
  const { t } = useTranslation('student_portal');

  const {
    status,
    heldToTime,
    countDown,
    paymentItems,
    partiallyPaidByBankTransferBeforeCheckIn,
  } = booking;
  const endTimeAsMoment = useMemo(() => moment(countDown), [countDown]);
  const countDownResult = useCountdownText(endTimeAsMoment);

  const hasAwaitingPaymentItems = usePaymentItemsIsAwaiting(paymentItems);

  const renderHeldTime = useMemo(() => {
    if (!heldToTime) return;
    if (!hasAwaitingPaymentItems) {
      if (partiallyPaidByBankTransferBeforeCheckIn) {
        return (
          <Typography
            component="p"
            variant="body3"
            style={{ flex: 1, paddingLeft: 8, display: 'flex', flexDirection: 'column' }}
          >
            <span>
              <Trans i18nKey={'payment.bank_transfer_underpaid_countdown'} ns="student_portal">
                It is noticed that the payment
                <Typography
                  variant="body3"
                  component="span"
                  color="var(--color-red_6)"
                  fontWeight={600}
                >
                  {{ time: moment(heldToTime).format('lll') }}
                </Typography>
              </Trans>
            </span>
            <Typography textDecoration="underline" cursor="pointer" variant="body3">
              {t('go_to_payment')}
            </Typography>
          </Typography>
        );
      }
      return (
        <Typography component="p" variant="body3" style={{ flex: 1, paddingLeft: 8 }}>
          <Trans i18nKey="hold_on.time" ns="public">
            Your bed has been reserved till
            <Typography
              variant="body3"
              component="span"
              color="var(--color-red_6)"
              fontWeight={600}
            >
              {{ time: moment(heldToTime).format('lll') }}
            </Typography>
          </Trans>
        </Typography>
      );
    }
    return (
      <Typography component="p" variant="body3" style={{ flex: 1, paddingLeft: 8 }}>
        <Trans i18nKey="hold_on.time" ns="public">
          Your bed has been reserved till
          <Typography variant="body3" component="span" color="var(--color-red_6)" fontWeight={600}>
            {{ time: moment(heldToTime).format('lll') }}
          </Typography>
        </Trans>
        <Typography component="span" variant="body3">
          {t('payment.bank_transfer.countDown_awaiting')}
        </Typography>
      </Typography>
    );
  }, [hasAwaitingPaymentItems, heldToTime, partiallyPaidByBankTransferBeforeCheckIn, t]);

  const renderCountDown = useMemo(() => {
    if (heldToTime || !countDown) return;

    if (!hasAwaitingPaymentItems) {
      if (status === BookingStatusOption.TERMS_ACCEPTED) {
        if (partiallyPaidByBankTransferBeforeCheckIn) {
          return (
            <Typography
              component="p"
              variant="body3"
              style={{ flex: 1, paddingLeft: 8, display: 'flex', flexDirection: 'column' }}
            >
              <span>
                <Trans i18nKey={'payment.bank_transfer_underpaid_countdown'} ns="student_portal">
                  It is noticed that the payment
                  <Typography
                    variant="body3"
                    component="span"
                    color="var(--color-red_6)"
                    fontWeight={600}
                  >
                    {{ time: countDownResult.trim() === '' ? '0s' : countDownResult }}
                  </Typography>
                </Trans>
              </span>
              <Typography textDecoration="underline" cursor="pointer" variant="body3">
                {t('go_to_payment')}
              </Typography>
            </Typography>
          );
        }
        return (
          <Typography component="p" variant="body3" style={{ flex: 1, paddingLeft: 8 }}>
            <Trans i18nKey="count_down.time.payment" ns="public">
              Please make your payment within
              <Typography
                variant="body3"
                component="span"
                color="var(--color-red_6)"
                fontWeight={600}
              >
                {{ time: countDownResult.trim() === '' ? '0s' : countDownResult }}
              </Typography>
              to confirm your booking.
            </Trans>
          </Typography>
        );
      }
      return (
        <Typography component="p" variant="body3" style={{ flex: 1, paddingLeft: 8 }}>
          <Trans i18nKey="count_down.time" ns="public">
            Your selected bed has been reserved for the next
            <Typography
              variant="body3"
              component="span"
              color="var(--color-red_6)"
              fontWeight={600}
            >
              {{ time: countDownResult.trim() === '' ? '0s' : countDownResult }}
            </Typography>
          </Trans>
        </Typography>
      );
    }
    return (
      <Typography component="p" variant="body3" style={{ flex: 1, paddingLeft: 8 }}>
        <Trans i18nKey="count_down.time.payment" ns="public">
          Please make your payment within
          <Typography variant="body3" component="span" color="var(--color-red_6)" fontWeight={600}>
            {{ time: countDownResult.trim() === '' ? '0s' : countDownResult }}
          </Typography>
          to confirm your booking.
        </Trans>
        <Typography component="span" variant="body3">
          {t('payment.bank_transfer.countDown_awaiting')}
        </Typography>
      </Typography>
    );
  }, [
    countDown,
    countDownResult,
    hasAwaitingPaymentItems,
    heldToTime,
    partiallyPaidByBankTransferBeforeCheckIn,
    status,
    t,
  ]);

  return (
    <div className="count-down-component">
      <div className="count-down-component__icon">
        <Time fill="var(--color-red_6)" />
      </div>
      {renderHeldTime}
      {renderCountDown}
      <style jsx>
        {`
          .count-down-component {
            width: 100%;
            display: flex;
            align-items: flex-start;
            padding: 10px 12px;
            border-radius: 14px;
            background-color: var(--color-red_1);
            margin-top: 12px;
            &__icon {
              width: 20px;
              height: 19px;
              margin-top: 2px;
            }
          }
          @media screen and (min-width: 768px) {
            .count-down-component {
              padding: 16px;
              border-radius: 14px;
            }
          }
        `}
      </style>
    </div>
  );
};

const BookingProgressCardCountdown: React.FC<IProps & React.HTMLAttributes<HTMLDivElement>> = ({
  booking,
}) => {
  const { category, countDown, heldToTime } = booking;
  return (
    <Fragment>
      {category === CategoryOption.NOMINATION && (countDown || heldToTime) && (
        <NominationCountdown booking={booking} />
      )}
      {category === CategoryOption.DIRECT_LET && (countDown || heldToTime) && (
        <DirectLetCountDown booking={booking} />
      )}
    </Fragment>
  );
};

export default BookingProgressCardCountdown;
